import { useMemo } from "react";
import useBranches from "@/features/users/api/useBranches";
import { useGlobalCfFields } from "@/features/customField/api/useGlobalCfFields";
import { resolveReportWindow } from "../hooks/computePrevWindow";
import {
  REPORT_TIMEFRAME_OPTIONS,
  useReportActiveTab,
  useReportBranch,
  useReportCompare,
  useReportGlobalCf,
  useReportIntakes,
  useReportTimeframe,
} from "../state/ReportsV2Context";
import { useAppSelector } from "@/stores/hook";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-01" → "1 Oct 2026" (the string is already an IST day). */
export function formatYmd(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
  if (!m) return ymd ?? "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

/** Intake key "<year>-<month0>" → "Sep 2026". */
function intakeLabel(key: string): string {
  const [y, m] = key.split("-");
  const month = MONTHS[Number(m)];
  return month && y ? `${month} ${y}` : key;
}

/**
 * The Reports page's filter bar, in words — printed under the title of every
 * shared PDF (and in its WhatsApp message) so a number read away from the
 * screen still says what it counts. Card-level filters (basis, outcome,
 * branch pickers) sit in the card's own header and are captured with it.
 */
export function useReportFilterSummary(): string[] {
  const timeframe = useReportTimeframe();
  const customStart = useAppSelector((s) => s.reportsV2.customStart);
  const customEnd = useAppSelector((s) => s.reportsV2.customEnd);
  const intakes = useReportIntakes();
  const globalCf = useReportGlobalCf();
  const compare = useReportCompare();
  const branch = useReportBranch();
  const activeTab = useReportActiveTab();
  const { branchSelects } = useBranches();
  const { fields } = useGlobalCfFields();

  return useMemo(() => {
    const lines: string[] = [];
    const preset = REPORT_TIMEFRAME_OPTIONS.find((o) => o.key === timeframe)?.label ?? "All time";
    const window = resolveReportWindow(timeframe, customStart, customEnd);
    if (!window) {
      lines.push(`Period: ${timeframe === "custom" ? "All time (no dates picked)" : preset}`);
    } else {
      const range =
        window.startDate === window.endDate
          ? formatYmd(window.startDate)
          : `${formatYmd(window.startDate)} – ${formatYmd(window.endDate)}`;
      lines.push(timeframe === "custom" ? `Period: ${range}` : `Period: ${preset} (${range})`);
    }
    if (compare.isCompare) {
      lines.push(
        compare.type === "custom" && compare.startDate && compare.endDate
          ? `Compared with ${formatYmd(compare.startDate)} – ${formatYmd(compare.endDate)}`
          : "Compared with the previous period",
      );
    }
    if (intakes.length > 0) {
      lines.push(`Intakes: ${[...intakes].sort().map(intakeLabel).join(", ")}`);
    }
    for (const [fieldId, optionId] of Object.entries(globalCf)) {
      if (!optionId || optionId === "all") continue;
      const field = fields.find((f) => String(f.id) === fieldId);
      const option = field?.options.find((o) => String(o.id) === optionId);
      if (field) lines.push(`${field.displayName}: ${option?.value ?? optionId}`);
    }
    // The Branch pill exists on Call Analytics only (ReportFilterBar).
    if (activeTab === "call" && branch && branch !== "all") {
      const name = (branchSelects ?? []).find((b: { value: string; name: string }) => b.value === branch)?.name;
      lines.push(`Branch: ${name ?? branch}`);
    }
    return lines;
  }, [timeframe, customStart, customEnd, compare, intakes, globalCf, fields, activeTab, branch, branchSelects]);
}
